/**
 * Central registry for button / UI tracing (console + Microsoft Clarity + GTM dataLayer).
 * Add new entries here when instrumenting additional clicks.
 *
 * @typedef {{ clarityEvent: string, consoleMessage: string }} TracingEventDef
 */

/**
 * Reads the value of a cookie by name.
 * Returns an empty string when the cookie is absent or document is unavailable.
 *
 * @param {string} name
 * @returns {string}
 */
function getCookie(name) {
  if (typeof document === "undefined") return "";
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(name + "="));
  return match ? decodeURIComponent(match.split("=")[1]) : "";
}

/**
 * Pushes a standardized GTM event to window.dataLayer.
 * All interactions use a single `event: "ui_interaction"` trigger so GTM
 * needs only one trigger; the specific action is in `event_name`.
 * The active A/B variant (from the `ab_test` cookie) is always included as `ab_variant`.
 *
 * @param {string} eventName - The specific interaction name (e.g. "lease_terms_btn")
 * @param {Record<string, unknown>} [extra] - Optional extra keys for GTM data layer variables
 */
export function pushTracingDataLayer(eventName, extra = {}) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "ui_interaction",
    event_name: eventName,
    variant: getCookie("ab_test"),
    ...extra,
  });
}

/** @type {Record<string, TracingEventDef>} */
export const TRACING_EVENTS = {
  FLOORPLAN_APPLY_NOW_LANDING: {
    clarityEvent: "floorplan_landing_apply_now",
    consoleMessage: "floorplan landing page apply now button click",
  },
  FLOORPLAN_UNIT_LISTING_APPLY_NOW: {
    clarityEvent: "floorplan_unit_listing_apply_now",
    consoleMessage: "floorplan unit listing apply now click",
  },
  MOVEIN_MODAL_APPLY_NOW: {
    clarityEvent: "movein_modal_apply_now",
    consoleMessage: "Move in Date Modal apply now button click",
  },
  LEASE_TERMS_BTN: {
    clarityEvent: "lease_terms_btn",
    consoleMessage: "lease terms button click",
  },
  MOVEIN_MODAL_NEXT_BTN: {
    clarityEvent: "movein_modal_next_btn",
    consoleMessage: "move in date modal next button click",
  },
  LEASE_TERMS_SELECT_RADIO: {
    clarityEvent: "lease_terms_select_radio",
    consoleMessage: "lease terms radio option selected in move in modal",
  },
  FLOORPLANS_RETURN_TO_RESULTS: {
    clarityEvent: "return_to_results",
    consoleMessage: "floorplans return to results click from unit listing",
  },
  RESULT_PAGE_LOAD: {
    clarityEvent: "result_page_load",
    consoleMessage: "floorplans result page loaded",
  },
  AVAILABILITY_BTN_CLICK: {
    clarityEvent: "availability_btn",
    consoleMessage: "floorplans type listing availability button click",
  },
};

/**
 * @param {keyof typeof TRACING_EVENTS} eventKey
 */
export function traceEvent(eventKey) {
  const def = TRACING_EVENTS[eventKey];
  if (!def) return;
  console.log(def.consoleMessage);
  pushTracingDataLayer(def.clarityEvent);
  if (typeof window !== "undefined" && typeof window.clarity === "function") {
    window.clarity("event", def.clarityEvent);
  }
}

/**
 * Maps internal filter keys to human-readable Clarity event name segments.
 * @type {Record<string, string>}
 */
const FILTER_KEY_MAP = {
  bed: "bedrooms",
  moveInStartDate: "move_in_date",
  sqft: "square_footage",
  price: "max_price",
  sort: "sort_by",
};

/**
 * Fires a Clarity event for a filter change, encoding both the filter name
 * and the page context (type_listing or unit_listing) in the event name.
 *
 * Resulting event names follow the pattern:
 *   filter_<filter>_<context>
 * e.g. filter_bedrooms_type_listing, filter_move_in_date_unit_listing
 *
 * @param {string} filterKey - The filter key (bed, moveInStartDate, sqft, price, sort)
 * @param {boolean} isTypeListing - true when on the type listing page, false for unit listing
 */
export function traceFilterEvent(filterKey, isTypeListing) {
  traceFirstMeaningfulAction();
  const filterName = FILTER_KEY_MAP[filterKey] || filterKey;
  const context = isTypeListing ? "type_listing" : "unit_listing";
  const clarityEvent = `filter_${filterName}_${context}`;
  console.log(`filter ${filterName} changed on ${context}`);
  pushTracingDataLayer(clarityEvent, {
    filter_key: filterKey,
    filter_name: filterName,
    listing_context: context,
  });
  if (typeof window !== "undefined" && typeof window.clarity === "function") {
    window.clarity("event", clarityEvent);
  }
}

/**
 * Fires a one-time Clarity event marking the first meaningful interaction
 * the user makes on the floorplans page within the current session.
 * Meaningful actions: any filter change, Apply Now, View Details, Availability.
 * Subsequent calls within the same session are no-ops.
 */
export function traceFirstMeaningfulAction() {
  /* first_meaningful_action — paused for now (re-enable when needed)
  if (typeof window === "undefined") return;
  if (sessionStorage.getItem("firstMeaningfulActionTracked")) return;
  sessionStorage.setItem("firstMeaningfulActionTracked", "true");
  console.log("first meaningful action on floorplans");
  if (typeof window.clarity === "function") {
    window.clarity("event", "first_meaningful_action");
  }
  */
}
